import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Clock, Truck, Star } from "lucide-react";
import { useLang, localized } from "../i18n/LanguageContext";
import { SEO_PREFIX, FAQS } from "../i18n/translations";
import useSEO from "../hooks/useSEO";
import api from "../lib/api";
import BookingEngine from "../components/BookingEngine";
import ProductCard from "../components/ProductCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Stars } from "../components/Reviews";

// Local SEO page. seoSlug looks like "location-scooter-electrique-toulouse".
const CityPage = () => {
  const { seoSlug } = useParams();
  const { lang, tr } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const prefix = SEO_PREFIX[lang];
  const citySlug = seoSlug && seoSlug.startsWith(prefix + "-") ? seoSlug.slice(prefix.length + 1) : null;

  useEffect(() => {
    if (!citySlug) { setData(false); return; }
    api.get(`/cities/${citySlug}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [citySlug]);

  const city = data && data.city;
  useSEO({
    title: city ? `${tr.cityPage.rentIn} ${city.name} — MobiRider` : "MobiRider",
    description: city ? localized(city.intro, lang) : "",
    lang,
    jsonLd: city ? {
      "@context": "https://schema.org", "@type": "LocalBusiness",
      name: `MobiRider ${city.name}`, description: localized(city.intro, lang),
      areaServed: city.name, address: { "@type": "PostalAddress", addressLocality: city.name, addressCountry: city.country },
    } : null,
  });

  if (data === false) return <div className="max-w-7xl mx-auto px-6 py-24 text-center text-xl">404</div>;
  if (!data) return <div className="max-w-7xl mx-auto px-6 py-24 text-center text-xl">{tr.common.loading}</div>;

  const faqs = FAQS[lang] || FAQS.fr;
  const avg = data.reviews.length ? (data.reviews.reduce((a, r) => a + r.rating, 0) / data.reviews.length).toFixed(1) : 0;

  return (
    <div>
      <section className="relative h-[380px] overflow-hidden">
        <img src={city.hero_image} alt={city.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/60 to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <span className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full font-semibold mb-4"><MapPin className="w-4 h-4" /> {city.name}, {city.country}</span>
            <h1 className="font-heading font-extrabold text-4xl md:text-5xl text-white tracking-tight max-w-2xl">{tr.cityPage.rentIn} {city.name}</h1>
            <p className="text-xl text-white/90 mt-4 max-w-2xl leading-relaxed">{localized(city.intro, lang)}</p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 -mt-12 relative z-10">
        <BookingEngine fixedCityId={city.id} />
      </div>

      <div className="max-w-7xl mx-auto px-6 py-14 grid gap-6 md:grid-cols-3">
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex items-start gap-4">
          <Truck className="w-8 h-8 text-brand shrink-0" />
          <div><h3 className="font-heading font-bold text-lg text-ink">{tr.cityPage.deliveryInfo}</h3><p className="text-base text-ink-muted mt-1">{tr.flow.deliveryFee}: {city.delivery_fee?.toFixed(0)} € · {city.delivery_radius_km} km</p></div>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex items-start gap-4">
          <Clock className="w-8 h-8 text-brand shrink-0" />
          <div><h3 className="font-heading font-bold text-lg text-ink">{tr.contactPage.hours}</h3><p className="text-base text-ink-muted mt-1">{city.hours}</p></div>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex items-start gap-4">
          <Star className="w-8 h-8 text-amber-400 shrink-0" />
          <div><h3 className="font-heading font-bold text-lg text-ink">{avg} / 5</h3><p className="text-base text-ink-muted mt-1">{data.reviews.length} {tr.reviews.based}</p></div>
        </div>
      </div>

      {city.landmarks?.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 pb-4">
          <h2 className="font-heading font-bold text-2xl text-ink mb-4">{tr.cityPage.landmarks}</h2>
          <div className="flex flex-wrap gap-3">
            {city.landmarks.map((l) => <span key={l} className="bg-brand/10 text-brand px-4 py-2 rounded-full font-semibold text-base">{l}</span>)}
          </div>
        </div>
      )}

      <section className="max-w-7xl mx-auto px-6 py-12">
        <h2 className="font-heading font-bold text-3xl text-ink mb-8">{tr.cityPage.availableHere} {city.name}</h2>
        <div className="grid gap-7 md:grid-cols-3">
          {data.products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {data.reviews.length > 0 && (
        <section className="bg-white py-12">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="font-heading font-bold text-3xl text-ink mb-8">{tr.reviews.title} · {city.name}</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {data.reviews.map((r) => (
                <div key={r.id} className="bg-cream rounded-3xl p-6 border border-slate-100">
                  <Stars n={r.rating} />
                  <p className="text-lg text-ink my-3">“{r.comment}”</p>
                  <span className="text-sm font-semibold text-ink-muted">{r.author}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-3xl mx-auto px-6 py-14">
        <h2 className="font-heading font-bold text-3xl text-ink mb-8 text-center">{tr.faqTitle}</h2>
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`i${i}`} className="bg-white rounded-2xl border border-slate-100 px-6 shadow-sm">
              <AccordionTrigger className="text-lg font-semibold text-ink py-5 hover:no-underline text-left">{f.q}</AccordionTrigger>
              <AccordionContent className="text-lg text-ink-muted leading-relaxed pb-5">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
};

export default CityPage;
